import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import api from "@/lib/api";
import JobCard from "@/components/JobCard";
import NotificationPrefsCard from "@/components/NotificationPrefsCard";
import { Briefcase, User, ClipboardList, Plus, Users, Building2 } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [apps, setApps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);

  useEffect(() => {
    if (!user) return;
    if (user.role === "worker") {
      api.get("/applications/mine").then((r) => setApps(r.data)).catch(() => {});
      api.get("/jobs", { params: user.skills?.[0] ? { category: user.skills[0] } : {} }).then((r) => setRecentJobs(r.data.slice(0, 6)));
    } else if (user.role === "contractor") {
      api.get("/jobs/mine").then((r) => setJobs(r.data)).catch(() => {});
    } else if (user.role === "admin") {
      api.get("/admin/stats").then((r) => setStats(r.data));
      api.get("/admin/jobs").then((r) => setJobs(r.data.slice(0, 8)));
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) return null;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-bold uppercase tracking-wide text-sarthi-muted">{t(user.role)}</div>
          <h1 className="font-display text-3xl font-extrabold sm:text-4xl">
            {user.name || user.email || user.mobile}
          </h1>
        </div>
        {user.role === "contractor" && (
          <Link to="/post-job" data-testid="post-job-cta" className="sarthi-btn-primary inline-flex items-center gap-2 px-5 py-3 text-white">
            <Plus className="h-5 w-5" /> {t("post_job")}
          </Link>
        )}
      </div>

      {user.role === "worker" && (
        <>
          <div className="mb-8">
            <NotificationPrefsCard />
          </div>
          <section>
            <h2 className="font-display text-2xl font-extrabold">{t("my_applications")}</h2>
            {apps.length === 0 ? (
              <div className="sarthi-card mt-4 p-6 text-center text-sarthi-muted">
                No applications yet. <Link to="/jobs" className="font-bold text-sarthi-primary underline">{t("browse_jobs")}</Link>
              </div>
            ) : (
              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                {apps.map((a) => (
                  <div key={a.id} data-testid={`app-${a.id}`} className="sarthi-card p-4">
                    <div className="text-xs font-bold uppercase tracking-wide text-sarthi-primary">{t(`categories_names.${a.job_category}`)}</div>
                    <div className="font-display text-lg font-extrabold">{a.job_title}</div>
                    <div className="text-sm font-semibold text-sarthi-muted">{a.job_location} · Status: {a.status}</div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="mt-10">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-2xl font-extrabold">{t("browse_jobs")}</h2>
              <Link to="/jobs" className="sarthi-btn-outline px-4 py-2 text-sm">→</Link>
            </div>
            {recentJobs.length === 0 ? (
              <div className="text-sarthi-muted">{t("no_jobs")}</div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {recentJobs.map((j) => <JobCard key={j.id} job={j} />)}
              </div>
            )}
          </section>
        </>
      )}

      {user.role === "contractor" && (
        <>
          <div className="mb-8">
            <NotificationPrefsCard />
          </div>
          <section>
          <h2 className="font-display text-2xl font-extrabold">{t("my_jobs")}</h2>
          {jobs.length === 0 ? (
            <div className="sarthi-card mt-4 p-6 text-center text-sarthi-muted">
              No jobs yet. <Link to="/post-job" className="font-bold text-sarthi-primary underline">{t("post_job")}</Link>
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              {jobs.map((j) => (
                <Link to={`/contractor/jobs/${j.id}`} key={j.id} data-testid={`my-job-${j.id}`} className="sarthi-card sarthi-card-press p-5">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-bold uppercase tracking-wide text-sarthi-primary">{t(`categories_names.${j.category}`)}</div>
                    <div className={`rounded-full px-3 py-1 text-xs font-bold ${j.status === "closed" ? "bg-stone-200 text-stone-700" : "bg-green-100 text-green-800"}`}>
                      {t(j.status === "closed" ? "status_closed" : "status_open")}
                    </div>
                  </div>
                  <div className="mt-1 font-display text-xl font-extrabold">{j.title}</div>
                  <div className="mt-1 text-sm font-semibold text-sarthi-muted">{j.location} · ₹{j.salary_min}–₹{j.salary_max}</div>
                  <div className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-sarthi-secondary"><Users className="h-4 w-4"/> {j.applicants_count || 0} {t("applicants")}</div>
                </Link>
              ))}
            </div>
          )}
        </section>
        </>
      )}

      {user.role === "admin" && stats && (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            <StatCard label={t("workers")} value={stats.workers} icon={<User />} color="orange" />
            <StatCard label={t("contractor") + "s"} value={stats.contractors} icon={<Building2 />} color="blue" />
            <StatCard label={t("jobs")} value={stats.jobs} icon={<Briefcase />} color="green" />
            <StatCard label="Open" value={stats.open_jobs} icon={<Briefcase />} color="amber" />
            <StatCard label={t("applications")} value={stats.applications} icon={<ClipboardList />} color="purple" />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-5">
            <StatCard label="Enquiries" value={stats.enquiries || 0} icon={<ClipboardList />} color="indigo" />
            <StatCard label="New Enquiries" value={stats.new_enquiries || 0} icon={<ClipboardList />} color="orange" />
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link data-testid="admin-users-link" to="/admin/users" className="sarthi-btn-outline px-5 py-3">Manage {t("workers")} / {t("contractor")}</Link>
            <Link data-testid="admin-enquiries-link" to="/admin/enquiries" className="sarthi-btn-outline px-5 py-3">Contractor Enquiries {stats.new_enquiries ? `(${stats.new_enquiries} new)` : ""}</Link>
            <Link data-testid="admin-jobs-link" to="/jobs" className="sarthi-btn-outline px-5 py-3">All {t("jobs")}</Link>
          </div>
          <h2 className="mt-10 font-display text-2xl font-extrabold">Recent {t("jobs")}</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {jobs.map((j) => <JobCard key={j.id} job={j} />)}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, icon, color }) {
  return (
    <div className={`sarthi-card p-5 cat-${color}`}>
      <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-white/70">{icon}</div>
      <div className="font-display text-3xl font-black">{value}</div>
      <div className="text-sm font-bold uppercase tracking-wide">{label}</div>
    </div>
  );
}
